import { useState } from 'react';
import { playChime } from '@/lib/chime';

/**
 * Interactive tooth chart using FDI World Dental Federation numbering
 * (the system Botswana dental practices use).
 *
 *   Upper right  18 17 16 15 14 13 12 11 | 21 22 23 24 25 26 27 28  Upper left
 *   Lower right  48 47 46 45 44 43 42 41 | 31 32 33 34 35 36 37 38  Lower left
 *
 * Click a tooth to mark it affected; the parent receives the array of
 * selected FDI codes. State on the chart itself stays local for the
 * hover-paint behaviour. Sparkle micro-interaction fires when a tooth
 * is newly selected (audit Part 4 §2).
 */
const UPPER_R = ['18', '17', '16', '15', '14', '13', '12', '11'];
const UPPER_L = ['21', '22', '23', '24', '25', '26', '27', '28'];
const LOWER_L = ['31', '32', '33', '34', '35', '36', '37', '38'];
const LOWER_R = ['48', '47', '46', '45', '44', '43', '42', '41'];

export function ToothChart({
  selected,
  onChange,
}: {
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const [hover, setHover] = useState<string | null>(null);
  // FDI code → epoch ms when it was selected. Used to trigger one
  // sparkle burst per selection without re-firing on every render.
  const [sparkleAt, setSparkleAt] = useState<Record<string, number>>({});

  const toggle = (code: string) => {
    if (selected.includes(code)) {
      onChange(selected.filter((c) => c !== code));
    } else {
      onChange([...selected, code]);
      // Trigger sparkle + chime on NEW selection only — undoing
      // a mark shouldn't fire the celebration animation.
      setSparkleAt((s) => ({ ...s, [code]: Date.now() }));
      playChime('sparkle');
    }
  };

  const Tooth = ({ code }: { code: string }) => {
    const isSelected = selected.includes(code);
    const isHover = hover === code;
    const sparkleKey = sparkleAt[code];
    return (
      <button
        type="button"
        onClick={() => toggle(code)}
        onMouseEnter={() => setHover(code)}
        onMouseLeave={() => setHover(null)}
        className={`tooth-btn group relative flex h-10 w-7 flex-col items-center justify-end rounded-md transition-all duration-150 ${
          isSelected
            ? 'scale-105 bg-gradient-to-b from-amber-200 to-amber-400 shadow-md shadow-amber-700/20'
            : isHover
              ? 'scale-105 bg-sky-100'
              : 'bg-white hover:bg-sky-50'
        } border ${isSelected ? 'border-amber-500' : 'border-slate-200'} `}
        aria-label={`Tooth ${code}`}
        aria-pressed={isSelected}
      >
        <svg viewBox="0 0 20 28" className="h-7 w-5">
          <path
            d="M10 1 C 5 1, 2 4, 2 8 C 2 11, 3 13, 4 15 C 4 17, 5 22, 7 26 C 8 27, 10 27, 10 23 C 10 27, 12 27, 13 26 C 15 22, 16 17, 16 15 C 17 13, 18 11, 18 8 C 18 4, 15 1, 10 1 Z"
            fill={isSelected ? '#FBBF24' : '#FFFFFF'}
            stroke={isSelected ? '#B45309' : '#94A3B8'}
            strokeWidth="0.8"
            strokeLinejoin="round"
          />
        </svg>
        <span
          className={`absolute -bottom-4 font-mono text-[9px] tabular-nums ${isSelected ? 'font-semibold text-amber-800' : 'text-slate-500'}`}
        >
          {code}
        </span>

        {/* Sparkle burst — fires once per new selection, then unmounts
            naturally on next state change since the key changes. */}
        {sparkleKey !== undefined && (
          <span
            key={sparkleKey}
            className="tooth-sparkle pointer-events-none absolute inset-0 grid place-items-center text-amber-500"
            aria-hidden="true"
          >
            <svg viewBox="0 0 24 24" className="h-6 w-6">
              <path
                d="M12 2 L13.5 9 L20 10.5 L13.5 12 L12 19 L10.5 12 L4 10.5 L10.5 9 Z"
                fill="currentColor"
              />
            </svg>
          </span>
        )}
      </button>
    );
  };

  const Row = ({ codes }: { codes: string[] }) => (
    <div className="flex gap-0.5">
      {codes.map((c) => (
        <Tooth key={c} code={c} />
      ))}
    </div>
  );

  return (
    <div className="select-none rounded-lg border border-sky-100 bg-gradient-to-b from-sky-50/60 to-white p-4">
      <style>{`
        @keyframes tooth-sparkle-burst {
          0%   { opacity: 0; transform: scale(0.4) rotate(0deg); }
          40%  { opacity: 1; transform: scale(1.4) rotate(45deg); }
          100% { opacity: 0; transform: scale(2.2) rotate(90deg); }
        }
        @keyframes tooth-bounce {
          0%, 100% { transform: scale(1.05); }
          50%      { transform: scale(1.15); }
        }
        .tooth-sparkle {
          animation: tooth-sparkle-burst 700ms ease-out forwards;
          filter: drop-shadow(0 0 6px rgba(251,191,36,0.7));
        }
        .tooth-btn[aria-pressed="true"] {
          animation: tooth-bounce 220ms ease-out;
        }
        @media (prefers-reduced-motion: reduce) {
          .tooth-sparkle, .tooth-btn[aria-pressed="true"] { animation: none !important; }
        }
      `}</style>

      <div className="mb-2 flex items-center justify-between text-xs">
        <span className="text-muted">Tap teeth to mark affected (FDI numbering)</span>
        {selected.length > 0 && (
          <button
            type="button"
            onClick={() => onChange([])}
            className="text-muted hover:text-ink underline"
          >
            Clear ({selected.length})
          </button>
        )}
      </div>

      {/* Mouth-view layout: upper jaw on top, lower jaw underneath, midline gap */}
      <div className="space-y-1.5">
        <div className="flex items-end justify-center gap-3 pb-4">
          <Row codes={UPPER_R} />
          <Row codes={UPPER_L} />
        </div>
        <div className="border-t border-dashed border-slate-300" />
        <div className="flex items-start justify-center gap-3 pt-4">
          <Row codes={LOWER_R} />
          <Row codes={LOWER_L} />
        </div>
      </div>

      {selected.length > 0 && (
        <p className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <strong>Affected:</strong>{' '}
          <span className="font-mono">{[...selected].sort().join(', ')}</span>
        </p>
      )}
    </div>
  );
}
